<div class="admin-shell">
    <header class="admin-header">
        <nav class="admin-tabs" aria-label="Admin sections">
            <a role="link" tabindex="0" [class.active]="IsActive('admin-dashboard')" (click)="GoTo('admin-dashboard')" (keydown.enter)="GoTo('admin-dashboard')">Dashboard</a>
            <a role="link" tabindex="0" [class.active]="IsActive('admin-configuration')" (click)="GoTo('admin-configuration')" (keydown.enter)="GoTo('admin-configuration')">Configuration</a>
            <a role="link" tabindex="0" [class.active]="IsActive('admin-partners')" (click)="GoTo('admin-partners')" (keydown.enter)="GoTo('admin-partners')">Partner Links</a>
        </nav>
        <h1>Configuration</h1>
        <p>Live-edit MAPS ranking thresholds, result-set defaults, and feature flags. Changes take effect on the next Search.</p>
    </header>

    <p *ngIf="isLoading" class="status">Loading…</p>
    <p *ngIf="errorMessage" class="status error" role="alert">{{ errorMessage }}</p>
    <p *ngIf="successMessage" class="status success" role="status">{{ successMessage }}</p>

    <form *ngIf="!isLoading" [formGroup]="form" (ngSubmit)="Save()" class="config-form">
        <fieldset class="card">
            <legend>Ranking thresholds</legend>
            <p class="help">Boundaries between Strong / Medium / Weak match tiers. Range 0–1. Values are the AI's absolute relevance score for a candidate against the job description.</p>

            <div class="row">
                <label>
                    <span class="label">Strong ≥</span>
                    <input type="number" step="0.01" min="0" max="1" formControlName="tierStrong" />
                </label>
                <label>
                    <span class="label">Medium ≥</span>
                    <input type="number" step="0.01" min="0" max="1" formControlName="tierMedium" />
                </label>
            </div>
        </fieldset>

        <fieldset class="card">
            <legend>Result set</legend>
            <p class="help">Default candidate count when the client doesn't specify one (capped 1–100). Relevance cutoff trims the long tail; set to 0 to disable.</p>

            <div class="row">
                <label>
                    <span class="label">Default result count</span>
                    <input type="number" step="1" min="1" max="100" formControlName="defaultResultCount" />
                </label>
                <label>
                    <span class="label">Relevance cutoff</span>
                    <input type="number" step="0.01" min="0" max="1" formControlName="relevanceCutoff" />
                </label>
            </div>
        </fieldset>

        <fieldset class="card">
            <legend>Qualification weighting</legend>
            <p class="help">How strongly each preferred-qualification tier weights a candidate's rank when a qualification weighting is applied. Required qualifications have no weight — candidates assessed as not meeting one are excluded from the results. Positive numbers only; changes apply to subsequent searches, not existing results.</p>

            <div class="row">
                <label>
                    <span class="label">Very important &times;</span>
                    <input type="number" step="0.5" min="0.1" max="100" formControlName="qualMultiplierVeryImportant" />
                </label>
                <label>
                    <span class="label">Important &times;</span>
                    <input type="number" step="0.5" min="0.1" max="100" formControlName="qualMultiplierImportant" />
                </label>
                <label>
                    <span class="label">Nice to have &times;</span>
                    <input type="number" step="0.5" min="0.1" max="100" formControlName="qualMultiplierNiceToHave" />
                </label>
            </div>
        </fieldset>

        <fieldset class="card">
            <legend>AI context</legend>
            <p class="help">How much of each candidate's most-relevant publication text is given to the AI when it ranks candidates and writes the "Why suggested" summaries. Lower values keep the AI focused on titles and structured profile data; 0 omits the publication text entirely.</p>

            <label>
                <span class="label">Publication text budget: {{ form.value.publicationTextWordLimit }} words</span>
                <input type="range" min="0" max="1000" step="100" formControlName="publicationTextWordLimit" />
            </label>

            <label>
                <span class="label">Publications per candidate: {{ form.value.relevantPublicationCount }}</span>
                <input type="range" min="1" max="10" step="1" formControlName="relevantPublicationCount" />
                <span class="help inline">How many of each candidate's most-relevant publications the AI sees (1 = single publication).</span>
            </label>

            <label>
                <span class="label">Interdisciplinarity: max publications per candidate</span>
                <input type="number" step="1" min="5" max="100" formControlName="interdiscMaxPubs" />
                <span class="help inline">Only used when a job description explicitly asks for interdisciplinary experience: caps how many of each candidate's publications feed the topical-spread boost. Higher = more exact for prolific authors but slower (cost grows quadratically). 16 is the validated default; range 5–100.</span>
            </label>
        </fieldset>

        <fieldset class="card">
            <legend>Eligibility &amp; career stage</legend>
            <p class="help">How the tool determines a candidate's career stage, and whether explicit non-technical job requirements (career stage, minimum years, on-site location / visa) remove ineligible candidates outright.</p>

            <label>
                <span class="label">Career-stage source</span>
                <select formControlName="careerStageSource">
                    <option value="personJob">Infer from employment history (recommended)</option>
                    <option value="aguProfile">Use stored AGU profile value</option>
                </select>
                <span class="help inline">"Infer from employment history" derives the stage from the candidate's MAPS job records; "stored AGU value" uses the self-reported AGU profile field.</span>
            </label>

            <label class="toggle">
                <input type="checkbox" formControlName="showCareerStage" />
                <span class="label">Show career stage on results</span>
                <span class="help inline">When on, each candidate's career-stage chip appears on result cards; when off, the chips are hidden. Display-only — ranking is unaffected.</span>
            </label>

            <label class="toggle">
                <input type="checkbox" formControlName="hardDisqualify" />
                <span class="label">Disqualify candidates who fail stated location/visa requirements</span>
                <span class="help inline">When on, a "no visa sponsorship" / on-site posting excludes candidates whose recorded location is in another country. When off, such candidates are kept but flagged. Candidates with unknown location are never excluded.</span>
            </label>

            <label class="toggle">
                <input type="checkbox" formControlName="levelDisqualify" />
                <span class="label">Disqualify candidates above the role's seniority level</span>
                <span class="help inline">When on, candidates whose employment records confirm a career stage ABOVE the role's level (from the stated experience minimum, else the target stages) are excluded and listed in the Disqualified table; current students are excluded from non-student roles. More junior candidates are kept and flagged as a stretch — leveling up is fine. Candidates whose stage cannot be determined are never excluded. When off, over-level candidates are kept with a step-down flag.</span>
            </label>

            <label class="toggle">
                <input type="checkbox" formControlName="educationDisqualify" />
                <span class="label">Disqualify candidates below an explicit degree requirement</span>
                <span class="help inline">When on, a posting with an explicit degree requirement excludes candidates whose verified academic records document a lower highest degree. Candidates with no degree data are never excluded — absence of a record is not absence of a degree.</span>
            </label>

            <label class="toggle">
                <input type="checkbox" formControlName="orgCountryInference" />
                <span class="label">Flag likely-ineligible candidates by organization country</span>
                <span class="help inline">When on (default), eligibility-gated searches infer the country of the ORGANIZATION for candidates with no recorded location (one cached AI call per organization). A confirmed conflict adds a "confirm directly" note to Things to Look Into and nudges ordering down — it never changes a match tier and never excludes anyone. Nationality or citizenship is never inferred.</span>
            </label>
        </fieldset>

        <fieldset class="card">
            <legend>Recruiting feasibility</legend>
            <p class="help">Estimates how realistic recruiting each candidate into the role is (compensation alignment, relocation burden, sector transition) and nudges ordering accordingly. Never changes match tiers and never excludes anyone.</p>

            <label class="toggle">
                <input type="checkbox" formControlName="feasibilityEnabled" />
                <span class="label">Assess recruiting feasibility</span>
                <span class="help inline">When on (default), one AI call per candidate produces the estimate and a short note on the result card. When off, no calls run and ordering is unchanged.</span>
            </label>

            <label>
                <span class="label">Feasibility weight: {{ form.value.feasibilityWeight }}</span>
                <input type="range" min="0" max="1" step="0.05" formControlName="feasibilityWeight" />
                <span class="help inline">How strongly the feasibility estimate nudges ordering (0–1). 0 keeps the card notes but removes the ranking effect; 0.15 is the default nudge.</span>
            </label>
        </fieldset>

        <fieldset class="card">
            <legend>Features</legend>

            <label class="toggle">
                <input type="checkbox" formControlName="showPipelinePanelToAll" />
                <span class="label">Show pipeline panel to partner users</span>
                <span class="help inline">When off (default), only admins see the "Pipeline &amp; agent I/O" drill-down on the Search page.</span>
            </label>
        </fieldset>

        <fieldset class="card" data-testid="contact-settings">
            <legend>Contact &amp; messaging</legend>
            <p class="help">Controls the contact modal's email delivery. Real member delivery requires BOTH the mode below set to live AND the MAPS_CONTACT_LIVE_ENABLED server flag — with either missing, every message is redirected to the safety inbox instead.</p>

            <label>
                <span class="label">Delivery mode</span>
                <select formControlName="contactDeliveryMode">
                    <option value="dryrun">Dry run — nothing is sent anywhere (default)</option>
                    <option value="sandbox">Sandbox — SendGrid validates, nothing is delivered</option>
                    <option value="redirect">Redirect — delivered to the safety inbox only</option>
                    <option value="live">Live — real delivery to candidates (needs the server flag too)</option>
                </select>
                <span class="help inline">Unrecognized values fall back to dry run — never toward live.</span>
            </label>

            <label>
                <span class="label">From address</span>
                <input type="email" formControlName="contactFromAddress" placeholder="e.g. talent@agu.org" />
                <span class="help inline">Must be a SendGrid-verified sender. While empty, sandbox/redirect/live sends refuse (dry run still works).</span>
            </label>

            <label>
                <span class="label">From name</span>
                <input type="text" formControlName="contactFromName" maxlength="100" />
                <span class="help inline">The display name candidates see next to the From address.</span>
            </label>

            <label>
                <span class="label">Safety/redirect inbox</span>
                <input type="email" formControlName="contactRedirectAddress" />
                <span class="help inline">Where redirected test messages go. Leaving it empty falls back to the built-in safety inbox — never to a candidate.</span>
            </label>

            <label>
                <span class="label">Recipients per send</span>
                <input type="number" formControlName="contactSendCap" min="1" max="100" />
                <span class="help inline">Cap on how many candidates one message can be sent to (1–100).</span>
            </label>
        </fieldset>

        <div class="actions">
            <button type="Submit" class="primary" [disabled]="form.invalid || isSaving">
                {{ isSaving ? 'Saving…' : 'Save settings' }}
            </button>
        </div>
    </form>
</div>
